/**
 * Publishes a HUD band's measured height as a custom property.
 *
 * In the two rail layouts the bands float in the letterbox beside the canvas
 * and nothing needs to know how tall they are. In dock mode the canvas is
 * full-bleed, so each band is a row of the stack: the playfield starts where
 * the top band ends and the deck stops where the bottom band starts. Both
 * heights are content-driven — the stats wrap to two lines on a 320px phone,
 * and the wave bar grows by a 46px SEND button between rounds — so a constant
 * in the stylesheet is wrong at a third of the viewports in the matrix and
 * wrong at a different third one round later.
 *
 * `offsetHeight`, not `getBoundingClientRect`: the bands wear a `rotateX` on
 * the barrel in the rail layouts, and a transformed rect is a rendered size,
 * not a layout one. Only the layout number is any use for stacking boxes.
 *
 * Writing to `documentElement.style` beats the `:root` rule in `ui.css` on
 * cascade origin, so the fallback there covers the first frame and this covers
 * every frame after it.
 */
export function trackBandHeight(node: HTMLElement, prop: string): () => void {
  const root = document.documentElement;
  let last = -1;

  const publish = (): void => {
    const px = node.offsetHeight;
    // A band measured while it is `display: none` is zero, and a zero row
    // would collapse the playfield onto whatever is under it.
    if (px <= 0 || px === last) return;
    last = px;
    root.style.setProperty(prop, `${px}px`);
  };

  publish();

  if (typeof ResizeObserver === 'undefined') return () => {};
  const ro = new ResizeObserver(publish);
  ro.observe(node);
  return () => {
    ro.disconnect();
    root.style.removeProperty(prop);
  };
}
